import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import { apiClient } from '@/api/axiosClient';
import { useAuth } from '@/contexts/AuthContext';
import { useDateFormat } from '@/hooks/useDateFormat';
import { PERMISSIONS } from '@/types/auth';
import type { ApprovalInstanceDto } from '@/types/approvals';
import type { NotificationDto } from '@/types/notifications';

/** Two independent feeds in one dropdown: real pending-approval data (reuses the same
 * GET /approvals/pending and POST /approvals/{id}/action endpoints the Approval Matrix "act" flow
 * is built on, so approving/rejecting here has the exact same effect as doing it from that
 * screen) plus the persisted Notification feed (master-data changes, approval-level routing,
 * low-stock/expiry alerts). The Approvals section only renders for users who can actually act on
 * one; the Notifications section renders for everyone, since notifications aren't approval-gated. */
export default function NotificationBell() {
  const { t } = useTranslation('nav');
  const { hasPermission } = useAuth();
  const { formatDateTime } = useDateFormat();
  const queryClient = useQueryClient();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const canAct = hasPermission(PERMISSIONS.APPROVAL_MATRIX_ACT);

  const { data: pending = [] } = useQuery({
    queryKey: ['approvals', 'pending'],
    queryFn: async () => (await apiClient.get<ApprovalInstanceDto[]>('/approvals/pending')).data,
    enabled: canAct,
    refetchInterval: 60_000,
  });

  const { data: notifications = [] } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => (await apiClient.get<NotificationDto[]>('/notifications')).data,
    refetchInterval: 60_000,
  });
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const actMutation = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'Approved' | 'Rejected' }) =>
      apiClient.post(`/approvals/${id}/action`, { decision, comments: null }),
    // Refetch on failure too, not just success — a "this approval has already been actioned" error
    // means the cached pending list is stale (someone else, or a lost race from a double-click,
    // already resolved it), and without this the item would keep showing here with working-looking
    // buttons that fail the exact same way forever.
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['approvals', 'pending'] }),
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/notifications/${id}/read`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => apiClient.post('/notifications/read-all'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // A plain ref check closes the double-click race window immediately, synchronously, on the
  // very first click — actMutation.isPending alone isn't enough, since React batches the state
  // update that flips it and a fast double-click can fire both handlers before that commits,
  // sending two POSTs for the same instance and losing the second one to an optimistic-concurrency
  // conflict on the backend.
  const actInFlight = useRef(false);
  const act = (id: string, decision: 'Approved' | 'Rejected') => {
    if (actInFlight.current) return;
    actInFlight.current = true;
    actMutation.mutate(
      { id, decision },
      { onSettled: () => { actInFlight.current = false; } },
    );
  };

  const badgeCount = pending.length + unreadCount;

  return (
    <>
      <Tooltip title={t('notifications')}>
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
          <Badge badgeContent={badgeCount} color="error" max={9}>
            <NotificationsOutlinedIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { width: 380, maxWidth: '90vw' } } }}
      >
        {canAct && (
          // A plain Box, not a Fragment — Menu clones each of its direct children for keyboard
          // navigation and rejects a Fragment there (console error: "Menu doesn't accept a
          // Fragment as a child"), so this whole approvals block must be a single real element.
          <Box>
            <Typography variant="subtitle2" sx={{ px: 2, py: 1.25, fontWeight: 700 }}>
              {t('pendingApprovals')}
            </Typography>
            <Divider />

            {pending.length === 0 ? (
              <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('noNotifications')}
                </Typography>
              </Box>
            ) : (
              <Stack sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {pending.map((instance) => (
                  <Box key={instance.id} sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" fontWeight={600}>
                      {instance.transactionType}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {instance.totalValue != null ? `₹${instance.totalValue.toLocaleString()} · ` : ''}
                      {formatDateTime(instance.createdDate)}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={actMutation.isPending}
                        onClick={() => act(instance.id, 'Approved')}
                      >
                        {t('approve')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled={actMutation.isPending}
                        onClick={() => act(instance.id, 'Rejected')}
                      >
                        {t('reject')}
                      </Button>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}

            <Divider />
          </Box>
        )}

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.25 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {t('notifications')}
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={() => markAllReadMutation.mutate()} disabled={markAllReadMutation.isPending}>
              {t('markAllRead')}
            </Button>
          )}
        </Stack>
        <Divider />

        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('noNotifications')}
            </Typography>
          </Box>
        ) : (
          <Stack sx={{ maxHeight: 320, overflowY: 'auto' }}>
            {notifications.map((n) => (
              <Box
                key={n.id}
                onClick={() => !n.isRead && markReadMutation.mutate(n.id)}
                sx={{
                  px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', cursor: n.isRead ? 'default' : 'pointer',
                  bgcolor: n.isRead ? 'transparent' : 'action.hover',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" fontWeight={n.isRead ? 400 : 700} sx={{ flexGrow: 1 }}>
                    {n.title}
                  </Typography>
                  {n.severity === 'Warning' && <Chip size="small" label="!" color="warning" sx={{ height: 18, minWidth: 18 }} />}
                </Stack>
                <Typography variant="caption" color="text.secondary" component="p">
                  {n.message}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {formatDateTime(n.createdDate)}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Menu>
    </>
  );
}
